/**
 * طبقة الاتصال بالإشراف الإداري —
 * `GET /supervision/summary` · `/leaves` · `PATCH /leaves/{id}` ·
 * `GET|POST /supervision/warnings` · `POST .../decide`.
 *
 * 🔴 **الفجوة اللي الشاشة بتقفلها:** ١٨١ توقفًا دخلوا من الاستيراد
 *    و**مفيش أي طريق يقفل واحد منهم**. القايمة اللي بتكبر وما بتقلّش
 *    الفريق بيبطّل يبصّ لها.
 *
 * ⚠️ **`null` مش صفر.** `teacherRequests = null` معناها «القناة مش
 *    شغّالة» (تليجرام لسه بلا توكن)، والصفر معناه «مفيش طلبات» —
 *    والخلط بينهم بيخلّي الشاشة تكدب على المشرفة.
 */
import { ApiError, api } from "./client";
import { latinDigits } from "./registry";

type Raw = Record<string, unknown>;

function fail(path: string, reason: string): never {
  console.error(`[supervision] ${reason} (${path})`);
  throw new ApiError(
    502,
    { code: "EMPTY_RESPONSE", message_ar: "السيستم رجّع رد مش مفهوم" },
    null,
  );
}

function str(v: unknown): string {
  return typeof v === "string" ? latinDigits(v) : "";
}
function strOrNull(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? latinDigits(v) : null;
}
function num(v: unknown, d = 0): number {
  return typeof v === "number" && Number.isFinite(v) ? v : d;
}
/** ⚠️ بيفرّق بين «مش شغّال» (`null`) و«صفر» */
function numOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function obj(v: unknown): Raw {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Raw) : {};
}

/* ═══════════════════════ الملخّص ═══════════════════════ */

export type SupervisionSummary = {
  leavesNeedsFollowup: number;
  leavesOpen: number;
  leavesTotal: number;
  warningsPending: number;
  warningsTotal: number;
  /** `null` = القناة مش شغّالة (تليجرام بلا توكن) — **مش** صفر */
  teacherRequests: number | null;
};

export async function getSupervisionSummary(): Promise<SupervisionSummary> {
  const path = "/supervision/summary";
  const res = await api.get<unknown>(path);
  if (!res.data || typeof res.data !== "object") fail(path, "الملخّص رجع فاضي");
  const d = obj(res.data);
  return {
    leavesNeedsFollowup: num(d["leaves_needs_followup"]),
    leavesOpen: num(d["leaves_open"]),
    leavesTotal: num(d["leaves_total"]),
    warningsPending: num(d["warnings_pending"]),
    warningsTotal: num(d["warnings_total"]),
    teacherRequests: numOrNull(d["teacher_requests"]),
  };
}

/* ═══════════════════════ التوقفات ═══════════════════════ */

export type LeaveRow = {
  id: string;
  subject_type: string;
  subject_name_ar: string | null;
  type: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
  end_date_unknown: boolean;
  next_check_at: string | null;
  followup_overdue: boolean;
  reason_ar: string | null;
  /** الناقص بالعربي زي ما السيرفر بيحسبه — الشاشة ما بتخمّنش */
  missing_ar: string;
  version: number;
};

function leaveRow(v: unknown): LeaveRow {
  const r = obj(v);
  return {
    id: str(r["id"]),
    subject_type: str(r["subject_type"]),
    subject_name_ar: strOrNull(r["subject_name_ar"]),
    type: str(r["type"]),
    status: str(r["status"]),
    start_date: strOrNull(r["start_date"]),
    end_date: strOrNull(r["end_date"]),
    end_date_unknown: r["end_date_unknown"] === true,
    next_check_at: strOrNull(r["next_check_at"]),
    followup_overdue: r["followup_overdue"] === true,
    reason_ar: strOrNull(r["reason_ar"]),
    missing_ar: str(r["missing_ar"]),
    version: num(r["version"], 1),
  };
}

export type LeavesMeta = {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  segmentCounts: Record<string, number>;
};

export type LeaveSegment = "needs_followup" | "open" | "all";

/**
 * شرايح التوقفات — الكتالوج عايش هنا مش جوّه الشاشة، زي `LEAD_SEGMENTS`
 * بالظبط. عدم التناظر ده هو اللي خلّى حد يقدر يسمّي شريحتين في شاشتين
 * بنفس الاسم من غير ما يشوفهم جنب بعض.
 *
 * 🔑 **«متابعة مستحقة» مش «محتاج متابعة».** «متابعة» جذر حقيقي في دومين
 *    التوقفات (`follow_up.next_check_at` · عمود «المتابعة الجاية» · لوحة
 *    «متابعة التوقف»)، و«مستحقة» كلمة الكود نفسه (`_due_since` · «مرتّبين
 *    بالأقدم استحقاقًا») — فالشيب بيطابق مفتاح الترتيب المعلن.
 * ⛔ و«متأخرة» مرفوضة: المستورد معفي من موعد المتابعة **عن قصد** (اختراع
 *    موعد لـ212 سجل = 212 تذكير كاذب)، فالسجلات دي مالهاش موعد أصلًا —
 *    «متأخرة» بتحط لوم على ناس ما غلطوش.
 * ⛔ والمفاتيح قيم على السلك — `supervision.py:_seg_clause` بيرمي 400 على
 *    أي قيمة تانية.
 */
export const LEAVE_SEGMENTS: {
  key: LeaveSegment; chip: string; defAr?: string;
}[] = [
  {
    key: "needs_followup",
    chip: "متابعة مستحقة",
    defAr: "موعد متابعته عدّى، أو سايب بلا موعد ونهايته مش محددة",
  },
  { key: "open", chip: "المفتوحة" },
  { key: "all", chip: "الكل" },
];

export async function listSupervisionLeaves(
  opts: { segment?: LeaveSegment; subjectType?: string; q?: string;
          page?: number; perPage?: number } = {},
): Promise<{ rows: LeaveRow[]; meta: LeavesMeta }> {
  const path = "/supervision/leaves";
  const res = await api.get<unknown>(path, {
    query: {
      segment: opts.segment ?? "needs_followup",
      subject_type: opts.subjectType || undefined,
      q: opts.q || undefined,
      page: opts.page ?? 1,
      per_page: opts.perPage ?? 25,
    },
  });
  if (!Array.isArray(res.data)) fail(path, "قايمة التوقفات رجعت بشكل مش متوقع");
  const m = obj(res.meta);
  const sc = obj(m["segment_counts"]);
  return {
    rows: (res.data as unknown[]).map(leaveRow),
    meta: {
      page: num(m["page"], 1), perPage: num(m["per_page"], 25),
      total: num(m["total"]), totalPages: num(m["total_pages"], 1),
      segmentCounts: Object.fromEntries(
        Object.entries(sc).map(([k, v]) => [k, num(v)])),
    },
  };
}

export type LeavePatch = {
  end_date?: string;
  end_date_unknown?: boolean;
  next_check_at?: string;
  reason_ar?: string;
  status?: string;
};

/**
 * ⛔ **التعديل بلا نسخة ممنوع** — الشاشة دي بتتفتح من كذا مشرفة على نفس
 *    قايمة «متابعة مستحقة»، والتعديلان في نفس اللحظة التاني بيدهس الأول بصمت.
 */
export async function patchLeave(
  id: string, patch: LeavePatch, version: number,
): Promise<{ row: LeaveRow; toastAr: string }> {
  const res = await api.patch<unknown>(`/supervision/leaves/${id}`, patch,
                                       { ifMatch: version });
  const d = obj(res.data);
  return { row: leaveRow(d), toastAr: str(d["toast_ar"]) || "اتحفظ" };
}

/* ═══════════════════════ المخالفات ═══════════════════════ */

export type WarningRow = {
  id: string;
  subject_name_ar: string | null;
  kind: string;
  kind_ar: string;
  amount: string | null;
  reason_ar: string | null;
  rule_ref: string | null;
  status: string;
  status_ar: string;
  recorded_by_name_ar: string | null;
  decided_by_name_ar: string | null;
  decision_note_ar: string | null;
  created_at: string | null;
  version: number;
};

function warningRow(v: unknown): WarningRow {
  const r = obj(v);
  return {
    id: str(r["id"]),
    subject_name_ar: strOrNull(r["subject_name_ar"]),
    kind: str(r["kind"]), kind_ar: str(r["kind_ar"]),
    amount: strOrNull(r["amount"]),
    reason_ar: strOrNull(r["reason_ar"]),
    rule_ref: strOrNull(r["rule_ref"]),
    status: str(r["status"]), status_ar: str(r["status_ar"]),
    recorded_by_name_ar: strOrNull(r["recorded_by_name_ar"]),
    decided_by_name_ar: strOrNull(r["decided_by_name_ar"]),
    decision_note_ar: strOrNull(r["decision_note_ar"]),
    created_at: strOrNull(r["created_at"]),
    version: num(r["version"], 1),
  };
}

export async function listWarnings(
  opts: { status?: string; page?: number; perPage?: number } = {},
): Promise<{ rows: WarningRow[]; total: number; pending: number }> {
  const path = "/supervision/warnings";
  const res = await api.get<unknown>(path, {
    query: { status: opts.status || undefined, page: opts.page ?? 1,
             per_page: opts.perPage ?? 25 },
  });
  if (!Array.isArray(res.data)) fail(path, "قايمة البنود رجعت بشكل مش متوقع");
  const m = obj(res.meta);
  return {
    rows: (res.data as unknown[]).map(warningRow),
    total: num(m["total"]),
    pending: num(m["pending_count"]),
  };
}

export async function proposeWarning(body: {
  subject_ref: string; kind: string; reason_ar: string;
  rule_ref?: string; amount?: string;
}): Promise<{ row: WarningRow; toastAr: string }> {
  const res = await api.post<unknown>("/supervision/warnings", body);
  const d = obj(res.data);
  return { row: warningRow(d), toastAr: str(d["toast_ar"]) || "اتسجّل" };
}

/** 🔒 الاعتماد صلاحية تانية — اللي بيرصد المخالفة مش اللي بيحكم بيها */
export async function decideWarning(
  id: string, approve: boolean, version: number, noteAr?: string,
): Promise<{ row: WarningRow; toastAr: string }> {
  const res = await api.post<unknown>(`/supervision/warnings/${id}/decide`,
                                      { approve, note_ar: noteAr },
                                      { ifMatch: version });
  const d = obj(res.data);
  return { row: warningRow(d), toastAr: str(d["toast_ar"]) || "اتسجّل" };
}

/** أسماء المعلمين لمنتقي البند */
export async function searchTeachers(
  q: string, limit = 12,
): Promise<{ id: string; name_ar: string }[]> {
  const path = "/teachers";
  const res = await api.get<unknown>(path, { query: { q, page: 1, per_page: limit } });
  if (!Array.isArray(res.data)) fail(path, "قايمة المعلمين رجعت بشكل مش متوقع");
  return (res.data as unknown[]).map((r) => {
    const o = obj(r);
    return { id: str(o["id"]), name_ar: str(o["name_ar"]) };
  }).filter((t) => t.id);
}
